import { Widget } from "../../types";
import { initialWidgets } from "../../data/initialData";
import { WidgetKey, widgetsCatalog } from "../../data/widgets";
import type { LayoutType } from "./uiSlice";

const countForLayout = (layout: LayoutType): number => {
  switch (layout) {
    case "2-2":
      return 4;
    case "2-1":
    case "1-2":
      return 3;
    case "1-1":
      return 2;
    case "1":
      return 1;
    default:
      return 4;
  }
};

export interface WidgetSlice {
  // State
  widgets: Widget[];
  selectingWidgetId: string | null;

  // Suggestion flow
  isSuggestingWidget: boolean;
  suggestedWidgetKey: WidgetKey | null;
  suggestedIndex: number; // absolute index in widgets array (row start when suggesting 2x1)

  // Actions
  setWidgetType: (id: string, type: WidgetKey) => void;
  openWidgetSelector: (id: string) => void;
  closeWidgetSelector: () => void;

  // Suggestion actions
  startWidgetSuggestion: (widgetKey: WidgetKey) => void;
  cancelWidgetSuggestion: () => void;
  cycleSuggestedIndex: (direction: 1 | -1) => void;
  assignSuggestedToIndex: (
    index?: number,
    suggestedWidgetKey?: WidgetKey
  ) => void;

  // Layout-driven reconciliation
  reconcileWidgetsForLayouts: (
    leftLayout: LayoutType,
    rightLayout: LayoutType,
    prevLeftLayout?: LayoutType,
    prevRightLayout?: LayoutType
  ) => void;
}

export const createWidgetSlice = (set: any, get: any): WidgetSlice => ({
  // Initial state
  widgets: (() => {
    // Initialize to exactly the number of available slots so slot indices are contiguous
    try {
      const left = get().leftLayout as LayoutType | undefined;
      const right = get().rightLayout as LayoutType | undefined;
      if (left && right) {
        const total = countForLayout(left) + countForLayout(right);
        return initialWidgets.slice(0, total).map((w, idx) => ({
          ...w,
          id: String(idx + 1),
        }));
      }
    } catch {}
    return initialWidgets;
  })(),
  selectingWidgetId: null,
  isSuggestingWidget: false,
  suggestedWidgetKey: null,
  suggestedIndex: 0,

  // Actions
  setWidgetType: (id, type) =>
    set((state: any) => ({
      widgets: state.widgets.map((w: Widget) =>
        w.id === id ? { ...w, type } : w
      ),
      selectingWidgetId: null,
    })),

  openWidgetSelector: (id) => set({ selectingWidgetId: id }),
  closeWidgetSelector: () => set({ selectingWidgetId: null }),

  // Suggestion actions
  startWidgetSuggestion: (widgetKey) =>
    set((state: any) => {
      // For environment (2x1), snap to first row start index
      const computeFirstRowStart = (): number => {
        const left: LayoutType = state.leftLayout;
        const leftCount = countForLayout(left);
        // Prefer left top row if not already same widget
        const leftTop = 0;
        if (state.widgets[leftTop]?.type !== widgetKey) return leftTop;
        // Otherwise try right top row start
        const rightTop = leftCount;
        if (state.widgets[rightTop]?.type !== widgetKey) return rightTop;
        // Otherwise fallback to first index not the same
        const anyOther = state.widgets.findIndex(
          (w: Widget) => w.type !== widgetKey
        );
        return Math.max(0, anyOther);
      };

      return {
        isSuggestingWidget: true,
        suggestedWidgetKey: widgetKey,
        suggestedIndex: computeFirstRowStart(),
      };
    }),

  cancelWidgetSuggestion: () =>
    set({ isSuggestingWidget: false, suggestedWidgetKey: null }),

  cycleSuggestedIndex: (direction) =>
    set((state: any) => {
      // Cycle by rows, not by cells. Compute row start indices across both sides.
      const collectRowStarts = (): number[] => {
        const result: number[] = [];
        const left: LayoutType = state.leftLayout;
        const right: LayoutType = state.rightLayout;
        const leftCount = countForLayout(left);
        const pushPair = (topStart: number, bottomStart: number) => {
          result.push(topStart, bottomStart);
        };
        const mapLayout = (layout: LayoutType, base: number) => {
          switch (layout) {
            case "2-2":
              pushPair(base + 0, base + 2);
              break;
            case "2-1":
              pushPair(base + 0, base + 2);
              break;
            case "1-2":
              pushPair(base + 0, base + 1);
              break;
            case "1-1":
              pushPair(base + 0, base + 1);
              break;
            case "1":
              // Act as two 2x1 rows mapping to the same start
              pushPair(base + 0, base + 0);
              break;
            default:
              pushPair(base + 0, base + 2);
          }
        };
        mapLayout(left, 0);
        mapLayout(right, leftCount);
        // Ensure indices are within bounds
        return result.filter((i) => i >= 0 && i < state.widgets.length);
      };

      const rowStarts = collectRowStarts();
      const currentIdx = Math.max(0, rowStarts.indexOf(state.suggestedIndex));
      const nextRowIdx =
        (currentIdx + direction + rowStarts.length) % rowStarts.length;
      const next = rowStarts[nextRowIdx] ?? state.suggestedIndex;
      return { suggestedIndex: next };
    }),

  assignSuggestedToIndex: (index, suggestedWidgetKey) => {
    const state = get();
    const key = suggestedWidgetKey || state.suggestedWidgetKey;
    // eslint-disable-next-line no-console
    console.log("assignSuggestedToIndex", {
      index,
      statekey: state.suggestedWidgetKey,
      suggestedWidgetKey,
      key,
    });
    if (!key) return;

    let targetIndex = typeof index === "number" ? index : state.suggestedIndex;

    const getRowStarts = (
      layout: LayoutType,
      base: number
    ): [number, number] => {
      switch (layout) {
        case "2-2":
          return [base + 0, base + 2];
        case "2-1":
          return [base + 0, base + 2];
        case "1-2":
          return [base + 0, base + 1];
        case "1-1":
          return [base + 0, base + 1];
        case "1":
          return [base + 0, base + 0];
        default:
          return [base + 0, base + 2];
      }
    };

    const leftLayout: LayoutType = state.leftLayout;
    const rightLayout: LayoutType = state.rightLayout;
    const leftCount = countForLayout(leftLayout);
    const isLeftSide = targetIndex < leftCount;
    const sideLayout = isLeftSide ? leftLayout : rightLayout;
    const baseOffset = isLeftSide ? 0 : leftCount;
    const [topStart] = getRowStarts(sideLayout, baseOffset);
    const isTopRow = targetIndex === topStart;
    // const isBottomRow = targetIndex === bottomStart;

    const computeDesiredLayout = (
      layout: LayoutType,
      top: boolean
    ): LayoutType => {
      switch (layout) {
        case "2-2":
          return top ? "1-2" : "2-1";
        case "2-1":
          return top ? "1-1" : "2-1";
        case "1-2":
          return top ? "1-2" : "1-1";
        case "1-1":
          return "1-1";
        case "1":
          return "1-1";
        default:
          return layout;
      }
    };

    const desiredLayout = computeDesiredLayout(sideLayout, isTopRow);
    if (isLeftSide) {
      get().setLeftLayout(desiredLayout);
    } else {
      get().setRightLayout(desiredLayout);
    }

    // After layout change, recompute target index for the selected row
    const newLeftLayout: LayoutType = get().leftLayout;
    const newRightLayout: LayoutType = get().rightLayout;
    const newLeftCount = countForLayout(newLeftLayout);
    const newBase = isLeftSide ? 0 : newLeftCount;
    const newSideLayout = isLeftSide ? newLeftLayout : newRightLayout;
    const [newTop, newBottom] = getRowStarts(newSideLayout, newBase);
    targetIndex = isTopRow ? newTop : newBottom;

    set((curr: any) => {
      const widgets: Widget[] = curr.widgets.map((w: Widget, i: number) =>
        i === targetIndex ? { ...w, type: key } : w
      );
      // eslint-disable-next-line no-console
      console.log(
        `Assigned widget ${String(key)} to slot ${targetIndex + 1} (${
          widgetsCatalog[key as WidgetKey].label
        })`
      );
      return {
        widgets,
        isSuggestingWidget: false,
        suggestedWidgetKey: null,
      };
    });
  },

  // Ensure the widgets array matches the available slots and ordering for the
  // given left/right layouts. We special-case certain transitions to keep
  // intuitive slot mappings when rows merge/split.
  reconcileWidgetsForLayouts: (
    leftLayout: LayoutType,
    rightLayout: LayoutType,
    prevLeftLayout?: LayoutType,
    prevRightLayout?: LayoutType
  ) =>
    set((state: any) => {
      const countForLayout = (layout: LayoutType): number => {
        switch (layout) {
          case "2-2":
            return 4;
          case "2-1":
          case "1-2":
            return 3;
          case "1-1":
            return 2;
          case "1":
            return 1;
          default:
            return 4;
        }
      };

      const createDefaultWidget = (id: number): Widget => ({
        id: String(id),
        // Pick a reasonable default; can be changed by user suggestion/selection later
        type: WidgetKey.SPEEDLIMITS,
        alerts: 0,
        warnings: 0,
      });

      const adjustChunk = (
        chunk: Widget[],
        prevLayout: LayoutType | undefined,
        nextLayout: LayoutType
      ): Widget[] => {
        const prevCount = prevLayout
          ? countForLayout(prevLayout)
          : chunk.length;
        const nextCount = countForLayout(nextLayout);

        let nextChunk: Widget[] = [...chunk];

        // Reductions
        if (nextCount < prevCount) {
          const removeAt = (() => {
            // 4 -> 3
            if (prevCount === 4 && nextCount === 3) {
              if (nextLayout === "2-1") {
                return 3; // remove 4th when bottom merges
              }
              if (nextLayout === "1-2") {
                return 1; // remove 2nd when top merges
              }
            }
            // 3 -> 2
            if (prevCount === 3 && nextCount === 2) {
              // Prefer dropping the middle to keep first and last
              return 1;
            }
            // 2 -> 1
            if (prevCount === 2 && nextCount === 1) {
              return 1; // keep first
            }
            // Fallback: drop last
            return nextChunk.length - 1;
          })();
          nextChunk = nextChunk.filter((_, i) => i !== removeAt);
        }

        // Expansions
        if (nextCount > nextChunk.length) {
          const insertAtPositions: number[] = (() => {
            const needed = nextCount - nextChunk.length;
            const positions: number[] = [];
            // Handle splits in intuitive spots
            if (prevCount === 2 && nextCount === 3) {
              positions.push(1); // insert between 1 and 2
            } else if (prevCount === 3 && nextCount === 4) {
              if (prevLayout === "1-2" && nextLayout === "2-2") {
                positions.push(1); // restore missing top-right
              } else {
                positions.push(nextChunk.length); // restore missing bottom-right
              }
            } else if (prevCount === 1 && nextCount >= 2) {
              positions.push(1); // split the big one; add after first
            }
            // If still need more, append at end
            while (positions.length < needed) positions.push(nextChunk.length);
            return positions;
          })();

          // Perform insertions in order
          insertAtPositions.forEach((pos, idx) => {
            const absolutePos = Math.min(pos + idx, nextChunk.length);
            nextChunk = [
              ...nextChunk.slice(0, absolutePos),
              createDefaultWidget(0), // temp id; will be reassigned later
              ...nextChunk.slice(absolutePos),
            ];
          });
        }

        // If still mismatched, truncate or append as a final fallback
        if (nextChunk.length > nextCount) {
          nextChunk = nextChunk.slice(0, nextCount);
        }
        if (nextChunk.length < nextCount) {
          const toAdd = nextCount - nextChunk.length;
          nextChunk = nextChunk.concat(
            Array.from({ length: toAdd }).map(() => createDefaultWidget(0))
          );
        }

        return nextChunk;
      };

      const leftPrev = prevLeftLayout;
      const rightPrev = prevRightLayout;
      const leftPrevCount = leftPrev ? countForLayout(leftPrev) : 4;
      const rightPrevCount = rightPrev ? countForLayout(rightPrev) : 4;

      const leftChunk = state.widgets.slice(0, leftPrevCount);
      const rightChunk = state.widgets.slice(
        leftPrevCount,
        leftPrevCount + rightPrevCount
      );

      const nextLeftChunk = adjustChunk(leftChunk, leftPrev, leftLayout);
      const nextRightChunk = adjustChunk(rightChunk, rightPrev, rightLayout);

      // Recombine and reassign sequential IDs
      const combined: Widget[] = [...nextLeftChunk, ...nextRightChunk].map(
        (w, idx) => ({ ...w, id: String(idx + 1) })
      );

      // Keep suggestedIndex within bounds
      const suggestedIndex = Math.min(
        state.suggestedIndex,
        combined.length - 1
      );

      return {
        widgets: combined,
        suggestedIndex: suggestedIndex < 0 ? 0 : suggestedIndex,
      };
    }),
});
